import sql from "sql-template-tag";

import { Logger } from "@moment/logging";

import { type DocsListItem, DocsListItemSchema } from "../document-list/document-list.types";
import {
	type TemplateOnboardingConfig,
	TemplateOnboardingConfigSchema,
} from "../document/template-onboarding.types";
import { type Flag } from "../feature-flags/feature-flags.types";
import { type Settings, SettingsSchema } from "../settings/settings.types";
import { type TauriEvent, TauriEventSchema } from "../tauri-events/tauri-events.types";
import {
	type Workflow,
	type WorkflowRun,
	WorkflowRunSchema,
	WorkflowSchema,
} from "../workflows/workflows.types";
import { type Workspace } from "../workspaces/workspace.types";
import { type SQLiteService, getSQLite } from "./sqlite";

const logger = new Logger("cache");

const SETTINGS_LOCAL_STORAGE_KEY = "moment:settings";

const getBrowserSettings = (): Settings => {
	if (typeof window === "undefined") {
		return {};
	}

	try {
		const rawSettings = window.localStorage.getItem(SETTINGS_LOCAL_STORAGE_KEY);
		if (!rawSettings) {
			return {};
		}

		const result = SettingsSchema.safeParse(JSON.parse(rawSettings));
		if (!result.success) {
			logger.warn("Failed to parse browser settings cache", { error: result.error });
			return {};
		}

		return result.data;
	} catch (error) {
		logger.warn("Failed to read browser settings cache", { error });
		return {};
	}
};

const setBrowserSettings = (settings: Settings): void => {
	if (typeof window === "undefined") {
		return;
	}

	try {
		window.localStorage.setItem(SETTINGS_LOCAL_STORAGE_KEY, JSON.stringify(settings));
	} catch (error) {
		logger.warn("Failed to write browser settings cache", { error });
	}
};

export type CacheAPI = {
	pendingEvents: {
		getAll: () => Promise<{ id: number; event: TauriEvent }[]>;
		delete: (id: number) => Promise<void>;
	};
	documents: {
		getAll: () => Promise<Record<string, DocsListItem>>;
		upsert: (document: DocsListItem) => Promise<void>;
		delete: (documentId: string) => Promise<void>;
		upsertAll: (documents: DocsListItem[]) => Promise<void>;
	};
	featureFlags: {
		getAll: () => Promise<Partial<Record<Flag, boolean>>>;
		upsert: (flag: Flag, enabled: boolean) => Promise<void>;
	};
	workspaces: {
		getAll: () => Promise<Record<string, Workspace>>;
		upsert: (workspace: Workspace) => Promise<void>;
	};
	settings: {
		get: () => Promise<Settings>;
		update: (partial: Partial<Settings>) => Promise<void>;
	};
	templateOnboarding: {
		get: (documentId: string) => Promise<TemplateOnboardingConfig | null>;
		insert: (documentId: string, config: TemplateOnboardingConfig) => Promise<void>;
		delete: (documentId: string) => Promise<void>;
	};
	loginContext: {
		getUserId: () => Promise<string | null>;
		setUserId: (userId: string) => Promise<void>;
		clearUserData: () => Promise<void>;
	};
	workflows: {
		getAll: () => Promise<Record<string, Workflow>>;
		upsert: (workflow: Workflow) => Promise<void>;
		delete: (id: string) => Promise<void>;
		deleteByDocumentId: (documentId: string) => Promise<void>;
	};
	workflowRuns: {
		getAll: (automationId?: string) => Promise<WorkflowRun[]>;
		upsert: (run: WorkflowRun) => Promise<void>;
		delete: (id: string) => Promise<void>;
		cleanUpOrphanedRuns: () => Promise<number>;
	};
};

/** @internal Exported for testing only */
export const sqliteCacheAPI = (sqlite: SQLiteService): CacheAPI => ({
	pendingEvents: {
		getAll: () =>
			sqlite
				.query<{ id: number; event: string }[]>(sql`SELECT * FROM pending_events`)
				.then((rows) => {
					logger.debug("Fetched pending_events rows from SQLite", { rows });

					const events: { id: number; event: TauriEvent }[] = [];

					rows.forEach((row) => {
						if (!row) {
							return;
						}

						const { success, data } = TauriEventSchema.safeParse(JSON.parse(row.event));
						if (!success) {
							logger.error("Failed to parse pending event");
							return;
						}

						events.push({
							id: row.id,
							event: data,
						});
					});

					logger.debug("Pending events loaded", { events });

					return events;
				}),
		delete: (id: number) =>
			sqlite.execute(sql`DELETE FROM pending_events WHERE id = ${id}`).then(() => undefined),
	},
	documents: {
		getAll: () =>
			sqlite
				.query<{ id: number; value: string }[]>(sql`SELECT * FROM documents`)
				.then((rows) =>
					Object.fromEntries(
						rows
							.map((row) => {
								const value = JSON.parse(row.value);

								// It's important that we use safeParse here, otherwise if the cache fails because one
								// document has an invalid date for instance, the entire docs list will fail to load
								const { success, data, error } =
									DocsListItemSchema.safeParse(value);

								if (!success) {
									logger.error("Failed to parse document", { id: row.id, error });
									return [row.id, null];
								}

								return [row.id, data];
							})
							.filter(([_, data]) => data !== null)
					)
				),
		upsert: (document: DocsListItem) =>
			sqlite
				.execute(
					sql`INSERT INTO documents (id, value) VALUES (${document.id}, ${JSON.stringify(document)}) ON CONFLICT (id) DO UPDATE SET value = ${JSON.stringify(document)}`
				)
				.then(() => undefined),
		delete: (documentId: string) =>
			sqlite
				.execute(sql`DELETE FROM documents WHERE id = ${documentId}`)
				.then(() => undefined),
		upsertAll: (documents: DocsListItem[]) =>
			Promise.all(
				documents.map((document) =>
					sqlite.execute(
						sql`INSERT INTO documents (id, value) VALUES (${document.id}, ${JSON.stringify(document)}) ON CONFLICT (id) DO UPDATE SET value = ${JSON.stringify(document)}`
					)
				)
			).then(() => undefined),
	},
	featureFlags: {
		getAll: () =>
			sqlite
				.query<{ id: Flag; enabled: string }[]>(sql`SELECT * FROM feature_flags`)
				.then((rows) =>
					Object.fromEntries(rows.map((row) => [row.id, row.enabled === "true"]))
				),
		upsert: (flag, enabled: boolean) =>
			sqlite
				.execute(
					sql`INSERT INTO feature_flags (id, value) VALUES (${flag}, ${enabled}) ON CONFLICT (id) DO UPDATE SET value = ${enabled}`
				)
				.then(() => undefined),
	},
	workspaces: {
		getAll: () =>
			sqlite
				.query<{ id: string; value: string }[]>(sql`SELECT * FROM workspaces`)
				.then((rows) =>
					Object.fromEntries(rows.map((row) => [row.id, JSON.parse(row.value)]))
				),
		upsert: (workspace: Workspace) =>
			sqlite
				.execute(
					sql`INSERT INTO workspaces (id, value) VALUES (${workspace.id}, ${JSON.stringify(workspace)}) ON CONFLICT (id) DO UPDATE SET value = ${JSON.stringify(workspace)}`
				)
				.then(() => undefined),
	},
	settings: {
		get: () =>
			sqlite
				.query<{ value: string }[]>(sql`SELECT value FROM settings WHERE id = 1`)
				.then((rows) => {
					if (rows.length === 0) {
						return {};
					}
					const result = SettingsSchema.safeParse(JSON.parse(rows[0].value));
					return result.success ? result.data : {};
				}),
		update: async (partial: Partial<Settings>) => {
			const current = await sqlite
				.query<{ value: string }[]>(sql`SELECT value FROM settings WHERE id = 1`)
				.then((rows) => (rows.length > 0 ? JSON.parse(rows[0].value) : {}));
			const merged = JSON.stringify({ ...current, ...partial });
			await sqlite.execute(
				sql`INSERT INTO settings (id, value) VALUES (1, ${merged}) ON CONFLICT (id) DO UPDATE SET value = ${merged}`
			);
		},
	},
	templateOnboarding: {
		get: (documentId: string) =>
			sqlite
				.query<
					{ id: string; value: string }[]
				>(sql`SELECT value FROM template_onboarding WHERE id = ${documentId}`)
				.then((rows) => {
					if (rows.length === 0) {
						return null;
					}
					const result = TemplateOnboardingConfigSchema.safeParse(
						JSON.parse(rows[0].value)
					);
					if (!result.success) {
						logger.error("Failed to parse template onboarding config", {
							documentId,
							error: result.error,
						});
						return null;
					}
					return result.data;
				}),
		insert: (documentId: string, config: TemplateOnboardingConfig) =>
			sqlite
				.execute(
					sql`INSERT INTO template_onboarding (id, value) VALUES (${documentId}, ${JSON.stringify(config)}) ON CONFLICT (id) DO UPDATE SET value = ${JSON.stringify(config)}`
				)
				.then(() => undefined),
		delete: (documentId: string) =>
			sqlite
				.execute(sql`DELETE FROM template_onboarding WHERE id = ${documentId}`)
				.then(() => undefined),
	},
	loginContext: {
		getUserId: () =>
			sqlite
				.query<{ user_id: string }[]>(sql`SELECT user_id FROM login_context WHERE id = 1`)
				.then((rows) => (rows.length > 0 ? rows[0].user_id : null))
				.catch((error) => {
					logger.warn("login_context table unavailable, degrading gracefully", { error });
					return null;
				}),
		setUserId: (userId: string) =>
			sqlite
				.execute(
					sql`INSERT INTO login_context (id, user_id) VALUES (1, ${userId}) ON CONFLICT (id) DO UPDATE SET user_id = ${userId}`
				)
				.then(() => undefined),
		clearUserData: async () => {
			logger.info("Clearing stale user data from cache");
			await sqlite.execute(sql`DELETE FROM documents`);
			await sqlite.execute(sql`DELETE FROM workspaces`);
			await sqlite.execute(sql`DELETE FROM automations`);
			await sqlite.execute(sql`DELETE FROM automation_runs`);
		},
	},
	workflows: {
		getAll: () =>
			sqlite
				.query<{ id: string; value: string }[]>(sql`SELECT * FROM automations`)
				.then((rows) =>
					Object.fromEntries(
						rows
							.map((row) => {
								const { success, data, error } = WorkflowSchema.safeParse(
									JSON.parse(row.value)
								);

								if (!success) {
									logger.error("Failed to parse workflow", {
										id: row.id,
										error,
									});
									return [row.id, null];
								}

								return [row.id, data];
							})
							.filter(([_, data]) => data !== null)
					)
				),
		upsert: (workflow: Workflow) =>
			sqlite
				.execute(
					sql`INSERT INTO automations (id, value) VALUES (${workflow.id}, ${JSON.stringify(workflow)}) ON CONFLICT (id) DO UPDATE SET value = ${JSON.stringify(workflow)}`
				)
				.then(() => undefined),
		delete: async (id: string) => {
			await sqlite.execute(sql`DELETE FROM automations WHERE id = ${id}`);
			await sqlite.execute(
				sql`DELETE FROM automation_runs WHERE json_extract(value, '$.automationId') = ${id}`
			);
		},
		deleteByDocumentId: async (documentId: string) => {
			// Find all workflow IDs targeting this document
			const rows = await sqlite.query<{ id: string }[]>(
				sql`SELECT id FROM automations WHERE json_extract(value, '$.targetDocumentId') = ${documentId}`
			);
			for (const row of rows) {
				await sqlite.execute(
					sql`DELETE FROM automation_runs WHERE json_extract(value, '$.automationId') = ${row.id}`
				);
			}
			await sqlite.execute(
				sql`DELETE FROM automations WHERE json_extract(value, '$.targetDocumentId') = ${documentId}`
			);
		},
	},
	workflowRuns: {
		getAll: (automationId?: string) =>
			sqlite
				.query<
					{ id: string; value: string }[]
				>(automationId ? sql`SELECT * FROM automation_runs WHERE json_extract(value, '$.automationId') = ${automationId}` : sql`SELECT * FROM automation_runs`)
				.then((rows) =>
					rows
						.map((row) => {
							const { success, data, error } = WorkflowRunSchema.safeParse(
								JSON.parse(row.value)
							);

							if (!success) {
								logger.error("Failed to parse workflow run", {
									id: row.id,
									error,
								});
								return null;
							}

							return data;
						})
						.filter((data): data is WorkflowRun => data !== null)
				),
		upsert: (run: WorkflowRun) =>
			sqlite
				.execute(
					sql`INSERT INTO automation_runs (id, value) VALUES (${run.id}, ${JSON.stringify(run)}) ON CONFLICT (id) DO UPDATE SET value = ${JSON.stringify(run)}`
				)
				.then(() => undefined),
		delete: (id: string) =>
			sqlite.execute(sql`DELETE FROM automation_runs WHERE id = ${id}`).then(() => undefined),
		cleanUpOrphanedRuns: async () => {
			const now = new Date().toISOString();
			const result = await sqlite.execute(
				sql`UPDATE automation_runs SET value = json_set(json_set(value, '$.status', 'lost'), '$.completedAt', ${now}) WHERE json_extract(value, '$.status') IN ('running', 'pending')`
			);
			return result?.rowsAffected ?? 0;
		},
	},
});
const noCacheAPI: CacheAPI = {
	pendingEvents: {
		getAll: () => Promise.resolve([]),
		delete: () => Promise.resolve(),
	},
	documents: {
		getAll: () => Promise.resolve({}),
		upsert: () => Promise.resolve(),
		delete: () => Promise.resolve(),
		upsertAll: () => Promise.resolve(),
	},
	featureFlags: {
		getAll: () => Promise.resolve({}),
		upsert: () => Promise.resolve(),
	},
	workspaces: {
		getAll: () => Promise.resolve({}),
		upsert: () => Promise.resolve(),
	},
	settings: {
		get: () => Promise.resolve(getBrowserSettings()),
		update: (partial: Partial<Settings>) => {
			setBrowserSettings({ ...getBrowserSettings(), ...partial });
			return Promise.resolve();
		},
	},
	templateOnboarding: {
		get: () => Promise.resolve(null),
		insert: () => Promise.resolve(),
		delete: () => Promise.resolve(),
	},
	loginContext: {
		getUserId: () => Promise.resolve(null),
		setUserId: () => Promise.resolve(),
		clearUserData: () => Promise.resolve(),
	},
	workflows: {
		getAll: () => Promise.resolve({}),
		upsert: () => Promise.resolve(),
		delete: () => Promise.resolve(),
		deleteByDocumentId: () => Promise.resolve(),
	},
	workflowRuns: {
		getAll: () => Promise.resolve([]),
		upsert: () => Promise.resolve(),
		delete: () => Promise.resolve(),
		cleanUpOrphanedRuns: () => Promise.resolve(0),
	},
};

// Singleton instance cache
let cacheInstance: CacheAPI | undefined;

// Main getter with lazy initialization
export const getCache = (): CacheAPI => {
	if (cacheInstance) {
		return cacheInstance;
	}

	const sqlite = getSQLite();
	cacheInstance = sqlite ? sqliteCacheAPI(sqlite) : noCacheAPI;

	return cacheInstance;
};

// Reset for testing
export const resetCache = (): void => {
	cacheInstance = undefined;
};
